import { useState, useEffect } from 'react'
import { Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption } from '@headlessui/react'
import { Check, ChevronsUpDownIcon as ChevronUpDown } from 'lucide-react'
import { Loader2 } from 'lucide-react'

interface AsyncSelectProps {
  loadOptions: (inputValue: string) => Promise<any[]>;
  value?: any;
  onChange: (value: any) => void;
  className?: string;
  placeholder?: string;
}

export function AsyncSelect({ loadOptions, value, onChange, className, placeholder }: AsyncSelectProps) {
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let active = true

    if (!query) {
      return
    }

    setLoading(true)
    loadOptions(query).then((newOptions) => {
      if (active) {
        setOptions(newOptions)
        setLoading(false)
      }
    })

    return () => {
      active = false
    }
  }, [loadOptions, query])

  return (
    <Combobox value={value} onChange={onChange} nullable>
      <div className={`relative w-full ${className}`}>
        <ComboboxInput
          className="w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 text-sm leading-5 focus:ring-indigo-500 focus:border-indigo-500"
          displayValue={(person: any) => person?.label}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
        />

        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronUpDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </ComboboxButton>

        <ComboboxOptions className="z-10 absolute mt-1 w-full bg-white rounded-md max-h-60 overflow-y-auto text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {loading && (
            <div className="relative mt-1 w-full">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            </div>
          )}
          {options.length === 0 && !loading && (
            <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
              Nothing found.
            </div>
          )}
          {options.map((option) => (
            <ComboboxOption
              key={option.value}
              className={({ active }) =>
                `relative cursor-default select-none py-2 pl-10 pr-4 ${
                  active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                }`
              }
              value={option}
            >
              {({ selected, active }) => (
                <>
                  <span
                    className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                  >
                    {option.label}
                  </span>
                  {selected && (
                    <span
                      className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}
                    >
                      <Check className="h-5 w-5" aria-hidden="true" />
                    </span>
                  )}
                </>
              )}
            </ComboboxOption>
          ))}
        </ComboboxOptions>
      </div>
    </Combobox>
  )
}

